// src/pages/admin/AdminCargaMasiva.js
import React, { useState } from "react";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import AdminLayout from "../../components/admin/AdminLayout";

const API_URL = process.env.REACT_APP_API_URL || "https://crm-backend-5tk6.onrender.com";

// ──────────────────────────────────────────────────────────────
// Styled Components
// ──────────────────────────────────────────────────────────────
const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  margin-bottom: 20px;
  max-width: 900px;
`;

const Select = styled.select`
  padding: 9px 14px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-right: 12px;
  &:focus { outline: none; border-color: #c8102e; }
`;

const Btn = styled.button`
  padding: 9px 18px;
  border-radius: 8px;
  border: none;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.$secondary ? "#fff" : p.$success ? "#2e7d32" : "#c8102e"};
  color: ${p => p.$secondary ? "#444" : "#fff"};
  border: ${p => p.$secondary ? "1px solid #ddd" : "none"};
  margin-right: 8px;
  opacity: ${p => p.disabled ? 0.5 : 1};
  cursor: ${p => p.disabled ? "not-allowed" : "pointer"};
  &:hover:not(:disabled) { opacity: 0.88; }
`;

const DropZone = styled.div`
  border: 2px dashed ${p => p.$drag ? "#c8102e" : "#ddd"};
  border-radius: 10px;
  padding: 36px;
  text-align: center;
  color: #888;
  cursor: pointer;
  background: ${p => p.$drag ? "#fff5f5" : "#fafafa"};
  transition: all 0.2s;
  margin-top: 16px;
  p { margin: 8px 0 0; font-size: 0.85rem; }
`;

const PreviewTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  margin-top: 16px;
  th {
    background: #f9f9f9;
    padding: 8px 10px;
    text-align: left;
    border-bottom: 1px solid #eee;
    color: #555;
    white-space: nowrap;
  }
  td {
    padding: 7px 10px;
    border-bottom: 1px solid #f5f5f5;
    color: #333;
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const StatusCell = styled.td`
  text-align: center;
  font-size: 1rem;
`;

const ErrorList = styled.ul`
  margin: 2px 0 0;
  padding-left: 14px;
  font-size: 0.75rem;
  color: #c8102e;
`;

const ResumenBox = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const ResumenItem = styled.div`
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  background: ${p => p.$color || "#f5f5f5"};
  color: ${p => p.$text || "#333"};
`;

const AlertBox = styled.div`
  padding: 14px 18px;
  border-radius: 8px;
  font-size: 0.88rem;
  margin-bottom: 16px;
  background: ${p => p.$error ? "#fdecea" : p.$warn ? "#fff8e1" : "#e8f5e9"};
  color: ${p => p.$error ? "#c8102e" : p.$warn ? "#e65100" : "#2e7d32"};
  font-weight: 500;
`;

// ──────────────────────────────────────────────────────────────
// Constantes
// ──────────────────────────────────────────────────────────────
const CURP_REGEX = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/;

const MACHOTES = {
  CECAP:  ["folio","nombre","apellidoPat","apellidoMat","curp","delegacion_estatal","delegacion_local","curso","fecha","vigencia"],
  FONE:   ["folio","nombre","apellidoPat","apellidoMat","curp","delegacion_estatal","delegacion_local","curso","clave","fecha","vigencia"],
  APS:    ["folio","nombre","apellidoPat","apellidoMat","curp","delegacion_estatal","curso","clave","fecha"],
  CEC:    ["nombre_completo","curp","fecha_emision","estatus","nombre_curso","horas","fecha_inicio","fecha_fin","folio"],
  CENCAD: ["nombre_completo","curp","fecha_emision","estatus","nombre_curso","horas","fecha_inicio","fecha_fin","folio"],
};

// Campos requeridos por tipo
const REQUERIDOS = {
  CECAP:  ["folio","nombre","apellidoPat","curp","delegacion_estatal","curso","fecha","vigencia"],
  FONE:   ["folio","nombre","apellidoPat","curp","delegacion_estatal","curso","clave","fecha","vigencia"],
  APS:    ["folio","nombre","apellidoPat","curp","delegacion_estatal","curso","clave","fecha"],
  CEC:    ["nombre_completo","curp","fecha_emision","folio"],
  CENCAD: ["nombre_completo","curp","fecha_emision","folio"],
};

// Campos de fecha por tipo
const CAMPOS_FECHA = {
  CECAP:  ["fecha","vigencia"],
  FONE:   ["fecha","vigencia"],
  APS:    ["fecha"],
  CEC:    ["fecha_emision","fecha_inicio","fecha_fin"],
  CENCAD: ["fecha_emision","fecha_inicio","fecha_fin"],
};

// Campos de CURP por tipo
const CAMPOS_CURP = {
  CECAP: "curp", FONE: "curp", APS: "curp", CEC: "curp", CENCAD: "curp",
};

// Campo de folio/pk por tipo
const CAMPO_FOLIO = {
  CECAP: "folio", FONE: "folio", APS: "folio", CEC: "folio", CENCAD: "folio",
};

// ──────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────

// Convierte cualquier formato de fecha a YYYY-MM-DD
function parseFecha(valor) {
  if (!valor) return null;

  // Si es número (Excel serial date)
  if (typeof valor === "number") {
    const date = XLSX.SSF.parse_date_code(valor);
    if (date) {
      const m = String(date.m).padStart(2, "0");
      const d = String(date.d).padStart(2, "0");
      return `${date.y}-${m}-${d}`;
    }
  }

  const str = String(valor).trim();

  // Ya está en YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

  // DD/MM/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [d, m, y] = str.split("/");
    return `${y}-${m}-${d}`;
  }

  // MM/DD/YYYY
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const parts = str.split("/");
    return `${parts[2]}-${parts[0]}-${parts[1]}`;
  }

  // Intento genérico
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    return d.toISOString().split("T")[0];
  }

  return null;
}

function validarFila(fila, tipo, foliosEnExcel) {
  const errores = [];
  const requeridos = REQUERIDOS[tipo] || [];
  const camposFecha = CAMPOS_FECHA[tipo] || [];
  const campoCurp = CAMPOS_CURP[tipo];
  const campoFolio = CAMPO_FOLIO[tipo];

  // 1. Campos requeridos
  for (const campo of requeridos) {
    if (!fila[campo] || String(fila[campo]).trim() === "") {
      errores.push(`Campo "${campo}" vacío`);
    }
  }

  // 2. CURP
  if (campoCurp && fila[campoCurp]) {
    const curp = String(fila[campoCurp]).trim().toUpperCase();
    if (!CURP_REGEX.test(curp)) {
      errores.push(`CURP inválida: "${curp}"`);
    }
  }

  // 3. Fechas
  for (const campo of camposFecha) {
    if (fila[campo] !== undefined && fila[campo] !== "") {
      const fecha = parseFecha(fila[campo]);
      if (!fecha) {
        errores.push(`Fecha inválida en "${campo}": "${fila[campo]}"`);
      }
    }
  }

  // 4. Folio duplicado en el mismo Excel
  const folio = fila[campoFolio] ? String(fila[campoFolio]).trim() : null;
  if (folio && foliosEnExcel.filter(f => f === folio).length > 1) {
    errores.push(`Folio "${folio}" duplicado en el archivo`);
  }

  return errores;
}

// Normaliza fechas en un registro para enviar a la BD
function normalizarFechas(fila, tipo) {
  const camposFecha = CAMPOS_FECHA[tipo] || [];
  const normalizado = { ...fila };
  for (const campo of camposFecha) {
    if (normalizado[campo] !== undefined && normalizado[campo] !== "") {
      normalizado[campo] = parseFecha(normalizado[campo]) || normalizado[campo];
    }
  }
  // Normalizar CURP a mayúsculas
  const campoCurp = CAMPOS_CURP[tipo];
  if (campoCurp && normalizado[campoCurp]) {
    normalizado[campoCurp] = String(normalizado[campoCurp]).trim().toUpperCase();
  }
  return normalizado;
}

// ──────────────────────────────────────────────────────────────
// Componente principal
// ──────────────────────────────────────────────────────────────
export default function AdminCargaMasiva() {
  const [tipo, setTipo] = useState("CECAP");
  const [registros, setRegistros] = useState([]); // filas raw del Excel
  const [validados, setValidados] = useState([]); // [{fila, errores, duplicadoBD}]
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validando, setValidando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [nombreArchivo, setNombreArchivo] = useState("");

  const token = localStorage.getItem("admin_token");

  // ── Descargar machote ──────────────────────────────────────
  const descargarMachote = () => {
    const columnas = MACHOTES[tipo];
    const ws = XLSX.utils.aoa_to_sheet([columnas]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tipo);
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf]), `machote_${tipo}.xlsx`);
  };

  // ── Leer y validar Excel ──────────────────────────────────
  const procesarArchivo = async (file) => {
    if (!file) return;
    setResultado(null);
    setValidados([]);
    setNombreArchivo(file.name);

    const reader = new FileReader();
    reader.onload = async (e) => {
      const wb = XLSX.read(e.target.result, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "" });

      if (!json.length) return;
      setRegistros(json);

      // Validación local
      setValidando(true);
      const campoFolio = CAMPO_FOLIO[tipo];
      const foliosEnExcel = json.map(r => r[campoFolio] ? String(r[campoFolio]).trim() : "");

      const resultLocal = json.map(fila => ({
        fila,
        errores: validarFila(fila, tipo, foliosEnExcel),
        duplicadoBD: false,
      }));

      // Validación contra BD — manda todos los folios en un solo request
      const foliosValidos = resultLocal
        .filter(r => r.errores.length === 0)
        .map(r => String(r.fila[campoFolio]).trim())
        .filter(Boolean);

      if (foliosValidos.length > 0) {
        try {
          const res = await fetch(`${API_URL}/api/admin/verificar-folios`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ tipo, folios: foliosValidos }),
          });
          const data = await res.json();
          const duplicados = new Set(data.duplicados || []);

          resultLocal.forEach(r => {
            const folio = String(r.fila[campoFolio] || "").trim();
            if (duplicados.has(folio)) {
              r.duplicadoBD = true;
              r.errores.push(`Folio "${folio}" ya existe en la base de datos`);
            }
          });
        } catch {
          // Si falla la verificación BD, continuamos solo con validación local
        }
      }

      setValidados(resultLocal);
      setValidando(false);
    };
    reader.readAsArrayBuffer(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    procesarArchivo(e.dataTransfer.files[0]);
  };

  // ── Subir registros válidos ───────────────────────────────
  const subir = async () => {
    const validos = validados.filter(r => r.errores.length === 0);
    if (!validos.length) return;

    setLoading(true);
    setResultado(null);
    try {
      const registrosNormalizados = validos.map(r => normalizarFechas(r.fila, tipo));
      const res = await fetch(`${API_URL}/api/admin/carga-masiva/${tipo}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ registros: registrosNormalizados }),
      });
      const data = await res.json();
      setResultado(data);
      if (data.exitosos > 0) {
        setRegistros([]);
        setValidados([]);
        setNombreArchivo("");
      }
    } catch {
      alert("Error al subir registros.");
    } finally {
      setLoading(false);
    }
  };

  const limpiar = () => {
    setRegistros([]);
    setValidados([]);
    setResultado(null);
    setNombreArchivo("");
  };

  // ── Stats ─────────────────────────────────────────────────
  const totalFilas = validados.length;
  const totalValidos = validados.filter(r => r.errores.length === 0).length;
  const totalErrores = totalFilas - totalValidos;
  const todosValidos = totalFilas > 0 && totalErrores === 0;

  const columnas = registros.length ? Object.keys(registros[0]) : [];

  return (
    <AdminLayout titulo="Carga masiva (Excel)">
      {/* Paso 1 */}
      <Card>
        <h3 style={{ margin: "0 0 16px", fontSize: "0.95rem", color: "#333" }}>
          1. Selecciona el tipo y descarga el machote
        </h3>
        <Select value={tipo} onChange={e => { setTipo(e.target.value); limpiar(); }}>
          {Object.keys(MACHOTES).map(t => <option key={t}>{t}</option>)}
        </Select>
        <Btn $secondary onClick={descargarMachote}>⬇️ Descargar machote {tipo}</Btn>
      </Card>

      {/* Paso 2 */}
      <Card>
        <h3 style={{ margin: "0 0 8px", fontSize: "0.95rem", color: "#333" }}>
          2. Sube el archivo Excel con los registros
        </h3>
        <p style={{ margin: "0 0 4px", fontSize: "0.82rem", color: "#888" }}>
          Usa el mismo formato del machote. Las fechas deben estar en formato DD/MM/YYYY o YYYY-MM-DD.
        </p>
        <DropZone
          $drag={drag}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => document.getElementById("fileInput").click()}
        >
          📊 {nombreArchivo || "Arrastra tu archivo aquí o haz clic para seleccionar"}
          <p>.xlsx o .xls únicamente</p>
        </DropZone>
        <input
          id="fileInput"
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={e => procesarArchivo(e.target.files[0])}
        />
      </Card>

      {/* Paso 3 — Validación y previsualización */}
      {validando && (
        <Card>
          <p style={{ color: "#888", textAlign: "center" }}>⏳ Validando registros...</p>
        </Card>
      )}

      {validados.length > 0 && !validando && (
        <Card>
          <h3 style={{ margin: "0 0 16px", fontSize: "0.95rem", color: "#333" }}>
            3. Resultado de validación
          </h3>

          {/* Resumen */}
          <ResumenBox>
            <ResumenItem $color="#e8f5e9" $text="#2e7d32">
              ✅ {totalValidos} válidos
            </ResumenItem>
            {totalErrores > 0 && (
              <ResumenItem $color="#fdecea" $text="#c8102e">
                ❌ {totalErrores} con errores
              </ResumenItem>
            )}
            <ResumenItem $color="#f5f5f5" $text="#555">
              📄 {totalFilas} total
            </ResumenItem>
          </ResumenBox>

          {/* Mensaje principal */}
          {todosValidos ? (
            <AlertBox>
              ✅ Todos los registros son válidos y no hay duplicados. Puedes registrar los {totalValidos} registros.
            </AlertBox>
          ) : totalValidos > 0 ? (
            <AlertBox $warn>
              ⚠️ {totalValidos} registros válidos y {totalErrores} con errores. Solo se subirán los válidos.
            </AlertBox>
          ) : (
            <AlertBox $error>
              ❌ Ningún registro pasó la validación. Corrige el archivo y vuelve a intentarlo.
            </AlertBox>
          )}

          {/* Tabla */}
          <div style={{ overflowX: "auto" }}>
            <PreviewTable>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>Estado</th>
                  {columnas.map(c => <th key={c}>{c}</th>)}
                  <th>Observaciones</th>
                </tr>
              </thead>
              <tbody>
                {validados.map((r, i) => (
                  <tr key={i} style={{ background: r.errores.length ? "#fff8f8" : "transparent" }}>
                    <StatusCell>{r.errores.length === 0 ? "✅" : "❌"}</StatusCell>
                    {columnas.map(c => (
                      <td key={c} title={String(r.fila[c])}>
                        {String(r.fila[c]).substring(0, 25)}
                      </td>
                    ))}
                    <td>
                      {r.errores.length === 0 ? (
                        <span style={{ color: "#2e7d32", fontSize: "0.75rem" }}>OK</span>
                      ) : (
                        <ErrorList>
                          {r.errores.map((err, j) => <li key={j}>{err}</li>)}
                        </ErrorList>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </PreviewTable>
          </div>

          {/* Acciones */}
          <div style={{ marginTop: "16px", display: "flex", gap: "10px" }}>
            <Btn $secondary onClick={limpiar}>🗑️ Limpiar</Btn>
            <Btn
              $success
              onClick={subir}
              disabled={loading || totalValidos === 0}
            >
              {loading
                ? "Subiendo..."
                : `✅ Confirmar y subir ${totalValidos} registro${totalValidos !== 1 ? "s" : ""}`
              }
            </Btn>
          </div>
        </Card>
      )}

      {/* Resultado final */}
      {resultado && (
        <Card>
          <h3 style={{ margin: "0 0 12px", fontSize: "0.95rem", color: "#333" }}>Resultado</h3>
          <AlertBox>
            ✅ {resultado.exitosos} registros insertados correctamente.
          </AlertBox>
          {resultado.errores?.length > 0 && (
            <>
              <p style={{ color: "#c8102e", fontSize: "0.85rem", margin: "8px 0 4px" }}>
                {resultado.errores.length} errores al insertar:
              </p>
              {resultado.errores.map((e, i) => (
                <p key={i} style={{ fontSize: "0.8rem", color: "#666", margin: "2px 0" }}>
                  ❌ {e.folio} — {e.error}
                </p>
              ))}
            </>
          )}
        </Card>
      )}
    </AdminLayout>
  );
}